/**
 * Decoded-image cache for the next few shots.
 *
 * Fetching an image is not enough to avoid a blank frame: a large JPEG still
 * has to be DECODED when it first paints. `HTMLImageElement.decode()` does
 * that ahead of time; keeping the element referenced keeps the decoded bitmap
 * warm. Only a small window around the current shot is held, so long
 * episodes never load every original into memory.
 */
export class ImagePreloader {
  private cache = new Map<string, { img: HTMLImageElement; ready: Promise<boolean> }>();

  /** Start (or reuse) decoding `url`; resolves true when paintable, false on error. */
  load(url: string): Promise<boolean> {
    const hit = this.cache.get(url);
    if (hit) return hit.ready;
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    const ready = img
      .decode()
      .then(() => true)
      .catch(() => false);
    this.cache.set(url, { img, ready });
    return ready;
  }

  /** Resolve when decoded, or after `timeoutMs` so playback never stalls on a slow file. */
  async ready(url: string, timeoutMs: number): Promise<boolean> {
    return Promise.race([
      this.load(url),
      new Promise<boolean>((r) => window.setTimeout(() => r(false), timeoutMs)),
    ]);
  }

  /** Drop everything not in `keep`. */
  retain(keep: Set<string>) {
    for (const url of this.cache.keys()) {
      if (!keep.has(url)) this.cache.delete(url);
    }
  }

  clear() {
    this.cache.clear();
  }
}
